import axios from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useSetRecoilState } from "recoil";

import {
  apiBackgroundLoadingErrorsAtom,
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  computeTrackingStoreUpdate,
  createCatchErrorResult,
  createErrorResult,
  createErrorResultWithNoErrorDetails,
  createSuccessResult,
  debugModeToApiParam,
  editRideTrackingAF,
  errors,
  IApiError,
  IFetchOptions,
  isTrackingMissingRideError,
  ITrackingBatchRideTrackingsDto,
  ITrackingInfoDto,
  ITrackingStoreUpdateOptions,
  Log,
  normalizeErrors,
  rideTrackingAF,
  rideTrackingRenderAF,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  TrackingApi,
  useHasPermissions,
} from "@ryde/shared";

export interface IFetchTrackingOptions extends IFetchOptions {
  rideId: number;
}

const toStoreUpdateOptions = (
  lastTrackingOnly?: boolean,
): ITrackingStoreUpdateOptions | undefined =>
  lastTrackingOnly ? { lastTrackingOnly: true } : undefined;

export const useTrackingActions = () => {
  const { t } = useTranslation();
  const { hasPermissions: hasPermissionFetchTracking } = useHasPermissions({
    entity: "tracking",
    action: "read",
  });
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setBackgroundLoadingErrors = useSetRecoilState(
    apiBackgroundLoadingErrorsAtom,
  );
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const commitTrackingForRide = useRecoilCallback(
    ({ set, snapshot }) =>
      (
        rideId: number,
        incomingDtos: ITrackingInfoDto[],
        storeOptions?: ITrackingStoreUpdateOptions,
      ) => {
        if (!incomingDtos.length) {
          return;
        }

        const update = computeTrackingStoreUpdate({
          rideId,
          incomingDtos,
          prevDtos: snapshot.getLoadable(rideTrackingAF(rideId)).getValue(),
          prevRender: snapshot
            .getLoadable(rideTrackingRenderAF(rideId))
            .getValue(),
          options: storeOptions,
        });

        if (!update.changed) {
          return;
        }

        set(rideTrackingAF(rideId), update.mergedDtos);

        if (update.renderState) {
          set(rideTrackingRenderAF(rideId), update.renderState);
        }
      },
    [],
  );

  const setTrackingAtomData = useEvent(commitTrackingForRide);

  // Single-ride edit/detail view store: replace outright with the freshly fetched full trail (one-shot
  // fetch, no incremental merge) so it is never trimmed by the live-map poll on the shared store.
  const commitEditTrackingForRide = useRecoilCallback(
    ({ set }) =>
      (rideId: number, incomingDtos: ITrackingInfoDto[]) => {
        set(editRideTrackingAF(rideId), incomingDtos);
      },
    [],
  );

  const setEditTrackingAtomData = useEvent(commitEditTrackingForRide);

  const setTrackingBatchAtomData = useEvent(
    useRecoilCallback(
      ({ set, snapshot }) =>
        (
          rows: ITrackingBatchRideTrackingsDto[],
          storeOptions?: ITrackingStoreUpdateOptions,
        ) => {
          for (const row of rows) {
            const trackings = row.trackings ?? [];

            if (!trackings.length) {
              continue;
            }

            const update = computeTrackingStoreUpdate({
              rideId: row.rideId,
              incomingDtos: trackings,
              prevDtos: snapshot
                .getLoadable(rideTrackingAF(row.rideId))
                .getValue(),
              prevRender: snapshot
                .getLoadable(rideTrackingRenderAF(row.rideId))
                .getValue(),
              options: storeOptions,
            });

            if (!update.changed) {
              continue;
            }

            set(rideTrackingAF(row.rideId), update.mergedDtos);

            if (update.renderState) {
              set(rideTrackingRenderAF(row.rideId), update.renderState);
            }
          }
        },
      [],
    ),
  );

  const fetchRideTrackings = useEvent(
    async (
      rideId: number,
      fetchOptions?: IFetchOptions,
    ): Promise<SimpleResult<ITrackingInfoDto[]>> => {
      const {
        abortController,
        backgroundAction,
        debugMode,
        lastTrackingOnly,
        commitToEditStore,
      } = fetchOptions ?? {};
      const storeOptions = toStoreUpdateOptions(lastTrackingOnly);

      if (!hasPermissionFetchTracking) {
        Log.debug(
          `Skipping tracking fetch for ride ${rideId} - no read permission`,
        );
        return createSuccessResult([]);
      }

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);
          setLoading(true);
        }

        Log.debug(`Fetching tracking dtos for ride ${rideId}`);
        const apiResult = await TrackingApi.fetchTrackingInfos(
          rideId,
          {
            debugMode: debugModeToApiParam(debugMode),
            lastTrackingOnly,
          },
          abortController,
        );

        if (!apiResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse<ITrackingInfoDto[]>(t);
        }

        Log.debug(`Tracking  dto received for rideId rideId `, apiResult);
        const trackings = apiResult.data ?? [];
        // The single-ride edit/detail fetch commits its full trail to a dedicated store so the live-map
        // poll can't trim it; every other (live-map) fetch keeps using the shared store as before.
        if (commitToEditStore) {
          setEditTrackingAtomData(rideId, trackings);
        } else {
          setTrackingAtomData(rideId, trackings, storeOptions);
        }

        return createSuccessResult(trackings);
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return createErrorResult<ITrackingInfoDto[]>(
            errors.common.requestCanceled,
          );
        }

        // A ride that was just archived/deleted answers "Missing ride": expected, so no toast and no
        // stored error. Every other failure (network, 500, permissions) is still toasted.
        if (isTrackingMissingRideError(error)) {
          Log.warn(t(errors.ride.failedFetchingTrackingDTOs), error);
          return createCatchErrorResult<ITrackingInfoDto[]>(error, t);
        }

        Log.errorAndToast(t(errors.ride.failedFetchingTrackingDTOs), error);

        if (backgroundAction) {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        } else {
          setErrors(normalizeErrors(error as IApiError, t));
        }

        return createCatchErrorResult<ITrackingInfoDto[]>(error, t);
      } finally {
        if (!backgroundAction) {
          setLoading(false);
        }
      }
    },
  );

  const fetchRideTrackingsBatch = useEvent(
    async (
      rideIds: number[],
      fetchOptions?: IFetchOptions,
    ): Promise<SimpleResult<ITrackingBatchRideTrackingsDto[]>> => {
      const { abortController, backgroundAction, debugMode, lastTrackingOnly } =
        fetchOptions ?? {};
      const storeOptions = toStoreUpdateOptions(lastTrackingOnly);

      const ids = [...new Set(rideIds)].filter(id => id > 0);

      if (!ids.length) {
        return createErrorResultWithNoErrorDetails<
          ITrackingBatchRideTrackingsDto[]
        >();
      }

      if (!hasPermissionFetchTracking) {
        Log.debug(
          `Skipping batch tracking fetch for ${ids.length} rides - no read permission`,
        );
        return createSuccessResult([]);
      }

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);
          setLoading(true);
        }

        Log.debug(`Fetching tracking dtos for  ${ids.length} rides`);
        const apiResult = await TrackingApi.fetchTrackingInfosBatch({
          rideIds: ids,
          options: {
            debugMode: debugModeToApiParam(debugMode),
            lastTrackingOnly,
          },
          abortController,
        });

        if (!apiResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        Log.debug(`Tracking dtos received for ride ids`, apiResult);
        const rows = apiResult.data ?? [];

        setTrackingBatchAtomData(rows, storeOptions);

        return createSuccessResult(rows);
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return createErrorResult(
            errors.common.requestCanceled,
            "Axios, request canceled",
          );
        }

        // No "Missing ride" filter here: the batch endpoint skips archived/deleted ids instead of failing,
        // so anything caught is a real failure worth a toast.
        Log.errorAndToast(t(errors.ride.failedFetchingTrackingDTOs), error);

        if (backgroundAction) {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        } else {
          setErrors(normalizeErrors(error as IApiError, t));
        }

        return createCatchErrorResult(error, t);
      } finally {
        if (!backgroundAction) {
          setLoading(false);
        }
      }
    },
  );

  const fetchTrackingForEditingRide = useEvent((rideId: number | undefined) => {
    if (!rideId) {
      return;
    }

    fetchRideTrackings(rideId, {
      backgroundAction: true,
      commitToEditStore: true,
    });
  });

  return {
    fetchRideTrackings,
    fetchRideTrackingsBatch,
    fetchTrackingForEditingRide,
    setTrackingAtomData,
  };
};
